/**
 * Example usage
  <Field
    as={FormikModalInput}
    label="Databases"
    name="databases"
    placeholder="Select a database..."
    disabled={loading}
  />
 */
import React from 'react';
import { AbstractButton, AbstractButtonProps, Box, FormError, MultiComboboxProps } from 'pounceV2';
import { FieldConfig } from 'formik';
import useFastField from 'Hooks/useFastField';
import useModalV3 from 'Hooks/useModalV3';
import { FieldFilterTypes } from 'Components/forms/DetectionFieldFiltersForm/constants';
import { useTheme } from '@mui/material';
import MultiValueModalV3 from './MultiValueModalV3';
import ModalInputTriggerV3 from './TriggerV3';

export type FormikModalInputProps<T> = Omit<
  ModalInputProps<T>,
  'value' | 'onChange' | 'error' | 'invalid'
>;

export type ModalInputProps<T> = {
  onChange: (value: T[]) => void;
  /**
   * The error, if any
   */
  error?: string;
  /**
   * A function that runs before a custom item is added by the user. If it returns `true`, then this
   * item will be added to the selection. If not, then this item won't be added
   * */
  validateAddition?: (userEnteredInput: T, existing: T[], splitOnComma: boolean) => boolean;
  arrayType?: FieldFilterTypes;
  triggerComponent?: React.ElementType;
} & Required<Pick<MultiComboboxProps<T>, 'value'>> &
  Pick<MultiComboboxProps<T>, 'invalid' | 'label' | 'placeholder' | 'disabled'> &
  Required<Pick<FieldConfig, 'name'>> &
  Partial<AbstractButtonProps>;

export function ModalInputV3<T extends React.ReactNode = any>(
  props: ModalInputProps<T>,
): React.ReactElement<ModalInputProps<T>> {
  const {
    palette: { componentTokens },
  } = useTheme();
  const {
    value,
    onChange,
    invalid: isInvalid,
    name,
    disabled,
    label,
    placeholder,
    error,
    validateAddition,
    arrayType = FieldFilterTypes.STRING_ARRAY,
    triggerComponent: TriggerComponent = ModalInputTriggerV3,
    ...rest
  } = props;

  const { showModal } = useModalV3();
  const items = value as T[];
  const errorElementId = isInvalid ? `${name}-error` : undefined;

  // We are preventing users for closing the modal by clicking outside
  // or click Escape, this a chunky modal and we don't want user lose their changes
  // due to misclicks. Clicking X will still close the modal and discard changes.
  // FIXME: Task to make this prop in modal manager
  // https://app.asana.com/0/1200879509676555/1203775457849961/f
  const preventModalDismiss = React.useCallback(
    (event?: React.SyntheticEvent) => event?.preventDefault(),
    [],
  );

  const onClick = React.useCallback(() => {
    showModal(
      <MultiValueModalV3
        initialItems={items}
        onSubmit={onChange}
        validateAddition={validateAddition}
        arrayType={arrayType}
      />,
      {
        title: placeholder,
        showCloseButton: true,
        onDismiss: preventModalDismiss,
      },
    );
  }, [showModal, items, onChange, validateAddition, arrayType, placeholder, preventModalDismiss]);
  const borderColor = React.useMemo(() => {
    if (isInvalid) {
      return componentTokens.buttons.colors.coreIcon.destructive.stroke.resting;
    }
    return componentTokens.buttons.colors.coreIcon.secondary.stroke.resting;
  }, [
    isInvalid,
    componentTokens.buttons.colors.coreIcon.destructive.stroke.resting,
    componentTokens.buttons.colors.coreIcon.secondary.stroke.resting,
  ]);

  return (
    <Box>
      <AbstractButton
        aria-label="Open Multi Value Modal Input"
        minHeight={47}
        overflow="auto"
        position="relative"
        border="1px solid"
        transition="border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
        backgroundColor="transparent"
        borderRadius="medium"
        borderColor={borderColor}
        aria-disabled={disabled}
        _hover={{
          borderColor: !isInvalid
            ? componentTokens.buttons.colors.coreIcon.primary.stroke.hover
            : undefined,
        }}
        _focusWithin={{
          borderColor: !isInvalid
            ? componentTokens.buttons.colors.coreIcon.primary.stroke.hover
            : undefined,
          span: {
            opacity: 1,
            visibility: 'visible',
          },
        }}
        width="100%"
        onClick={onClick}
        {...rest}
      >
        <TriggerComponent
          value={value}
          label={label}
          disabled={disabled}
          invalid={isInvalid}
          placeholder={placeholder}
        />
      </AbstractButton>
      {isInvalid && (
        <FormError mt={2} id={errorElementId}>
          {error}
        </FormError>
      )}
    </Box>
  );
}

function FormikModalInputV3<T>(props: FormikModalInputProps<T>): React.ReactNode {
  const { name } = props;
  const [{ value }, meta, { setValue }] = useFastField<T[]>(name);
  const isInvalid = meta.touched && !!meta.error;
  return (
    <ModalInputV3
      invalid={isInvalid}
      value={value as any[]}
      onChange={setValue}
      error={meta.error}
      {...props}
    />
  );
}

export default FormikModalInputV3;
